'use client';

import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import { type ComponentPropsWithRef, useEffect, useId, useState } from 'react';

import BaseContainer from '@commons/containers/BaseContainer';

import Label from '../Label';
import styles from './index.module.scss';

/**
 * Textarea component props.
 */

export type TextareaProps = ComponentPropsWithRef<'textarea'> & {
  name: string;
  rows?: number;
  error?: boolean;
  required?: boolean;
  label?: string;
  variant?: 'primary' | 'secondary';
};

/**
 * Textarea component.
 *
 * This export can be used as an uncontrolled component or with a custom controller.
 */

export const Textarea = (props: TextareaProps) => {
  const generatedId = useId();
  const {
    id,
    error = false,
    className,
    label,
    rows = 1,
    variant = 'primary',
    required = false,
    ref,
    ...rest
  } = props;

  const [hasValue, setHasValue] = useState(!!rest.value);

  const fieldId = id ?? generatedId;

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setHasValue(!!event.target.value);
  };

  const handleFocus = () => {
    setHasValue(true);
  };

  const classes = clsx(styles.Textarea, styles[variant], error && styles.error, className);

  return (
    <BaseContainer className={styles.wrapper}>
      <textarea
        id={fieldId}
        ref={ref}
        rows={rows}
        className={classes}
        aria-required={required || undefined}
        onFocus={handleFocus}
        onBlurCapture={handleBlur}
        {...rest}
      />

      {label && (
        <Label
          htmlFor={fieldId}
          active={hasValue}
          required={required}
          error={error}
          className={clsx(styles.label, styles[variant], hasValue && styles.shrink)}
        >
          {label}
        </Label>
      )}
    </BaseContainer>
  );
};

/**
 * Formik-controlled Textarea component.
 *
 * This export should be used inside a Formik form.
 */

export const FormikTextarea = ({ name, label, ...props }: TextareaProps) => {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();

  const isError = meta.touched && meta.error !== undefined;

  // This effect will check if there's a value in the URL for this field
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const urlValue = searchParams.get(name);

      if (urlValue) {
        setFieldValue(name, urlValue);
      }
    }
  }, [name, setFieldValue]);

  return <Textarea rows={1} error={isError} label={label} {...field} {...props} />;
};

export default Textarea;
